import { formatTime } from "@/lib/i18n";
import { formatDate, formatRelative } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Date renderers. Simplified port of the Next-side `local-date.tsx`
 * — no SSR means no UTC-then-local two-step. The browser is always
 * the renderer, so we format directly in the user's local timezone
 * and skip the hydration-mismatch dance.
 *
 * `LocalRelative` keeps the 60 s tick so "2m ago" labels stay live
 * without a parent re-render.
 */

type Props = {
  value: Date | string;
  className?: string;
  withTime?: boolean;
};

function toIso(v: Date | string): string {
  return typeof v === "string" ? v : v.toISOString();
}

export function LocalDate({ value, className, withTime = false }: Props) {
  const iso = toIso(value);
  return (
    <time dateTime={iso} className={className}>
      {formatDate(iso, undefined, { withTime })}
    </time>
  );
}

export function LocalTime({ value, className }: Props) {
  const iso = toIso(value);
  return (
    <time dateTime={iso} className={className}>
      {formatTime(iso)}
    </time>
  );
}

export function LocalRelative({ value, className }: Props) {
  const iso = toIso(value);
  // Reading the language re-renders the label when it changes.
  const { i18n } = useTranslation();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  void tick;
  void i18n.language;
  const text = formatRelative(iso);
  return (
    <time dateTime={iso} className={className}>
      {text}
    </time>
  );
}
